import axios from "axios";

import { toast } from "react-toastify";
import { env } from "./env";
import { logApiError } from "./utils/ErrorLog";

const baseLink = env.REACT_APP_BASE_URL
  ? env.REACT_APP_BASE_URL
  : "https://popsv3-try.azurewebsites.net/";
// const baseLink = "https://popsv3.relysoft.in/";

const localUrl = JSON.parse(localStorage.getItem("LocalURL"));
function CheckFirstSlash(str) {
  const first = str.substring(0, 1);
  if (first !== "/") {
    return "/" + str;
  }
  return str;
}

// Renew the token this far before it actually expires, so a request that
// is about to be sent is never sent with a token that dies in flight.
const EXPIRY_SKEW_MS = 90 * 1000;

// Keys that must survive a forced logout. A customer who is bumped out
// because their token could not be renewed should still find their cart
// (and the failure records) waiting when they sign back in.
const PRESERVED_ON_LOGOUT = [
  "selcted-Products",
  "selcted-Products-orderbyComp",
  "api-error-log",
  "failed-order-log",
];

function clearStorageKeepingCart() {
  const keep = {};
  PRESERVED_ON_LOGOUT.forEach((key) => {
    try {
      const value = localStorage.getItem(key);
      if (value !== null) keep[key] = value;
    } catch (e) {
      /* ignore */
    }
  });
  try {
    localStorage.clear();
  } catch (e) {
    /* ignore */
  }
  Object.keys(keep).forEach((key) => {
    try {
      localStorage.setItem(key, keep[key]);
    } catch (e) {
      /* ignore */
    }
  });
}

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user-info"));
  } catch (e) {
    return null;
  }
}

function getToken() {
  const user = getStoredUser();
  return user && user.token ? user.token : "";
}

// Read the "exp" claim straight out of the JWT so we know when it dies
// without having to wait for the server to tell us with a 401.
function decodeJwtExpiry(token) {
  try {
    const parts = String(token).split(".");
    if (parts.length < 2) return 0;
    let payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    while (payload.length % 4 !== 0) payload += "=";
    const decoded = window.atob(payload);
    const claims = JSON.parse(decoded);
    return claims && claims.exp ? claims.exp * 1000 : 0;
  } catch (e) {
    return 0;
  }
}

/** True when the current token is missing, already dead, or about to die. */
function tokenNeedsRenewal() {
  const token = getToken();
  if (!token) return false; // nothing to renew — let the call go out as-is
  const expiresAt = decodeJwtExpiry(token);
  if (!expiresAt) return false; // unreadable expiry — fall back to 401 handling
  return Date.now() + EXPIRY_SKEW_MS >= expiresAt;
}

function refreshUrl() {
  const appType = localStorage.getItem("app-type");
  if (!appType) return null;
  // Matches the login convention: AuthTokenCust -> RefreshTokenCust
  return (
    baseLink + "api/" + appType + "/Authenticate/RefreshToken" + appType
  );
}

// A single in-flight refresh shared by every caller. Without this, a page
// with several widgets fires one refresh per 401 and they invalidate each
// other's refresh token.
let refreshPromise = null;

function renewToken() {
  if (refreshPromise) return refreshPromise;

  const users = getStoredUser();
  const url = refreshUrl();

  if (!users || !users.refreshToken || !url) {
    return Promise.reject(new Error("No refresh token available"));
  }

  refreshPromise = axios
    .post(url, {
      ExpiredToken: users.token,
      RefreshToken: users.refreshToken,
    })
    .then((res) => {
      if (res && res.data && res.data.isSuccess && res.data.token) {
        localStorage.setItem("user-info", JSON.stringify(res.data));
        return res.data.token;
      }
      const reason =
        (res && res.data && (res.data.reason || res.data.message)) ||
        "Refresh rejected";
      logApiError({
        method: "POST",
        url: url,
        data: { ExpiredToken: "[redacted]", RefreshToken: "[redacted]" },
        error: { message: "Token refresh failed: " + reason, response: res },
        label: "RefreshToken",
      });
      throw new Error(reason);
    })
    .catch((err) => {
      if (err && err.response) {
        logApiError({
          method: "POST",
          url: url,
          data: { ExpiredToken: "[redacted]", RefreshToken: "[redacted]" },
          error: err,
          label: "RefreshToken",
        });
      }
      throw err;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

// Called when the session genuinely cannot be saved. The cart survives, so
// the customer finds their order waiting after signing back in.
function forceReLogin() {
  toast.error("Your session has expired. Please sign in again.");
  clearStorageKeepingCart();
  setTimeout(() => {
    window.location.replace("/");
  }, 1200);
}

function buildHeaders(token, isJson) {
  if (!token) return isJson ? { "Content-Type": "application/json" } : {};
  const headers = { Authorization: "Bearer " + token };
  if (isJson) headers["Content-Type"] = "application/json";
  return headers;
}

/**
 * One request, with the token handled for the caller:
 *   1. renew first if the token is about to expire,
 *   2. on a 401, renew and replay the request once,
 *   3. only give up (and send the user to login) if the renewal itself fails.
 *
 * The caller's promise stays pending across the renewal, so a submit that
 * hits an expired token now succeeds instead of surfacing as an error.
 */
async function request(options) {
  const {
    method,
    url,
    data,
    isJson,
    cancelToken,
    label,
    unwrapData,
  } = options;

  const send = (token) => {
    const config = {
      method: method,
      url: url,
      headers: buildHeaders(token, isJson),
      cancelToken: cancelToken ? cancelToken : false,
    };
    if (data !== undefined) config.data = data;
    return axios(config);
  };

  // Step 1 — pre-emptive renewal.
  let token = getToken();
  if (tokenNeedsRenewal()) {
    try {
      token = await renewToken();
    } catch (e) {
      // Could not renew up front; still try the call — the token may yet
      // be accepted, and if not the 401 path below handles it.
      token = getToken();
    }
  }

  try {
    const res = await send(token);
    return unwrapData && res.data ? res.data : res;
  } catch (err) {
    const status = err && err.response ? err.response.status : null;

    // Step 2 — replay once after a renewal.
    if (status === 401 && !axios.isCancel(err)) {
      try {
        const freshToken = await renewToken();
        const res = await send(freshToken);
        return unwrapData && res.data ? res.data : res;
      } catch (retryErr) {
        const retryStatus =
          retryErr && retryErr.response ? retryErr.response.status : null;

        logApiError({
          method: method.toUpperCase(),
          url: url,
          data: data,
          error: retryErr,
          label: label ? label + " (after token renewal)" : "after renewal",
        });

        // Step 3 — the renewal itself failed, or the replay was rejected
        // again. The session is genuinely dead.
        if (retryStatus === 401 || !retryStatus) {
          forceReLogin();
        }
        return Promise.reject(retryErr);
      }
    }

    logApiError({
      method: method.toUpperCase(),
      url: url,
      data: data,
      error: err,
      label: label,
    });
    return Promise.reject(err);
  }
}

// Api calling functions when call api call from this functions it makes it possible to be used like a component
// to be used again and again also be able to pass token validation
const GetApiHandler = (props) =>
  request({
    method: "get",
    url: baseLink + props.url,
    isJson: false,
    cancelToken: props.cancelToken,
    label: props.label,
    unwrapData: true,
  });

const PostApiHandler = (props) =>
  request({
    method: "post",
    url: props.useUserUrl === true ? props.url : baseLink + props.url,
    data: props.data,
    isJson: true,
    cancelToken: props.cancelToken,
    label: props.label,
    unwrapData: false,
  });

const PutApiHandler = (props) =>
  request({
    method: "put",
    url: baseLink + props.url,
    data: props.data,
    isJson: false,
    cancelToken: props.cancelToken,
    label: props.label,
    unwrapData: false,
  });

const DeleteApiHandler = (props) =>
  request({
    method: "delete",
    url: baseLink + props.url,
    isJson: false,
    cancelToken: props.cancelToken,
    label: props.label,
    unwrapData: false,
  });

const ApiHandler = {
  get: GetApiHandler,
  post: PostApiHandler,
  put: PutApiHandler,
  delete: DeleteApiHandler,
  cancelToken: axios.CancelToken,
  // Exposed so a screen can make sure the session is good before a long
  // form, rather than finding out at submit time.
  ensureFreshToken: () =>
    tokenNeedsRenewal() ? renewToken() : Promise.resolve(getToken()),
};

export default ApiHandler;
